import type { CefrLevel, QuizKind } from "@rmnlng/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

// Per-rule practice item — same render-shape as `QuizReviewCard` minus
// the SRS context (no `state` / `preview` / `isNew`). The component
// surface (`<QuizCardSurface>`) reads only the rendering fields, so
// both shapes work interchangeably.
export type QuizPracticeCard = {
  quizItemId: string;
  quizKind: QuizKind;
  sentence: string;
  translation: string | null;
  sourceSentence: string | null;
  sourceTranslation: string | null;
  hints: string[];
  explanation: string | null;
  options?: string[];
  mode: "mcq" | "type";
};

export type PracticeSessionResponse = {
  rule: {
    id: string;
    title: string;
    level: CefrLevel | null;
  };
  items: QuizPracticeCard[];
};

export type PracticeAnswerResponse = {
  correct: boolean;
  expectedAnswers: string[];
  explanation: string | null;
};

/**
 * Fetch a rule's quiz items for a one-shot drill. Distinct from the
 * SRS `useReviewQueue` because:
 *   - Items always come from a specific rule, not interleaved.
 *   - No SRS context — every card starts as "new" each session.
 *   - Order is deterministic (insertion order) so re-drilling feels
 *     consistent across sessions.
 *
 * `staleTime: Infinity` because the items don't change mid-session.
 * Force a refetch via `refetchOnMount: "always"` so a curator's
 * mid-session edit (rare but possible) gets picked up next time.
 */
export function usePracticeSession(ruleId: string | undefined) {
  return useQuery({
    queryKey: ["grammar", "practice", ruleId ?? ""],
    queryFn: () => apiFetch<PracticeSessionResponse>(`/grammar/${ruleId}/practice`),
    enabled: !!ruleId,
    staleTime: Infinity,
    refetchOnMount: "always",
  });
}

/**
 * Pure stateless answer evaluation. Server returns whether the answer
 * matched + the expected answers + the optional explanation. **No DB
 * writes** — the user can re-drill the same rule daily without
 * polluting their SRS schedule.
 *
 * No `onSuccess` cache work because there's nothing to invalidate.
 */
export function useGradePracticeAnswer(ruleId: string | undefined) {
  return useMutation({
    mutationFn: (input: { quizItemId: string; userAnswer: string }) =>
      apiFetch<PracticeAnswerResponse>(`/grammar/${ruleId}/practice/answer`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}
